import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 间隔重复学习法动画 - 艾宾浩斯遗忘曲线
 * 
 * 核心效果：
 * 1. 遗忘曲线可视化
 * 2. 重复复习时间点
 * 3. 记忆保持率提升
 * 4. 最佳复习间隔建议
 */

export interface ReviewPoint {
  day: number;
  retention: number; // 记忆保持率 (0-1)
  label: string;
}

export interface SpacedRepetitionAnimationProps {
  title?: string;
  subtitle?: string;
  showCurve?: boolean;
  primaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
}

// 艾宾浩斯遗忘曲线公式: R = e^(-t/S)
// R = 记忆保持率, t = 时间, S = 记忆强度
const forgettingCurve = (t: number, S: number = 1): number => {
  return Math.exp(-t / S);
};

export const SpacedRepetitionAnimation: React.FC<SpacedRepetitionAnimationProps> = ({
  title = '艾宾浩斯遗忘曲线',
  subtitle = 'Ebbinghaus Forgetting Curve',
  showCurve = true,
  primaryColor = '#4A90E2',
  accentColor = '#F59E0B',
  backgroundColor = '#0F1419',
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 标题动画
  const titleOpacity = interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' });
  const titleY = spring({ frame, from: -50, to: 0, fps, config: { damping: 15 } });

  // 复习时间点
  const reviewPoints: ReviewPoint[] = [
    { day: 0, retention: 1.0, label: '第1次学习' },
    { day: 1, retention: 0.36, label: '第1天复习' },
    { day: 3, retention: 0.58, label: '第3天复习' },
    { day: 7, retention: 0.75, label: '第7天复习' },
    { day: 14, retention: 0.85, label: '第14天复习' },
    { day: 30, retention: 0.92, label: '第30天复习' },
  ];

  // 曲线动画
  const curveProgress = interpolate(frame, [60, 180], [0, 1], { extrapolateRight: 'clamp' });
  
  // 复习点动画
  const reviewPointProgress = interpolate(frame, [240, 420], [0, reviewPoints.length], { extrapolateRight: 'clamp' });

  // 绘制遗忘曲线
  const drawForgettingCurve = (): string => {
    const points: string[] = [];
    const maxDays = 30;
    const graphWidth = width * 0.6;
    const graphHeight = height * 0.28;
    const graphX = (width - graphWidth) / 2;
    const graphY = height * 0.35;

    for (let day = 0; day <= maxDays * curveProgress; day += 0.5) {
      const retention = forgettingCurve(day);
      const x = graphX + (day / maxDays) * graphWidth;
      const y = graphY + graphHeight - retention * graphHeight;
      
      if (day === 0) {
        points.push(`M ${x} ${y}`);
      } else {
        points.push(`L ${x} ${y}`);
      }
    }

    return points.join(' ');
  };

  // 绘制优化后的记忆曲线（有复习的情况）
  const drawOptimizedCurve = (): string => {
    const points: string[] = [];
    const maxDays = 30;
    const graphWidth = width * 0.6;
    const graphHeight = height * 0.28;
    const graphX = (width - graphWidth) / 2;
    const graphY = height * 0.35;

    // 简化的优化曲线（每次复习后记忆保持率提升）
    const optimizedRetention = [1.0, 0.9, 0.8, 0.75, 0.72, 0.70];
    
    for (let i = 0; i < Math.min(Math.floor(reviewPointProgress), reviewPoints.length); i++) {
      const retention = optimizedRetention[i] || 0.7;
      const x = graphX + (reviewPoints[i].day / maxDays) * graphWidth;
      const y = graphY + graphHeight - retention * graphHeight;
      
      if (i === 0) {
        points.push(`M ${x} ${y}`);
      } else {
        points.push(`L ${x} ${y}`);
      }
    }

    return points.join(' ');
  };

  return (
    <AbsoluteFill style={{
      background: `linear-gradient(135deg, ${backgroundColor} 0%, #1A1F3A 100%)`,
      fontFamily: 'system-ui, sans-serif',
      color: 'white',
      padding: 60,
    }}>
      {/* 标题 */}
      <div style={{
        position: 'absolute',
        top: 60,
        left: 0,
        right: 0,
        textAlign: 'center',
        opacity: titleOpacity,
        transform: `translateY(${titleY}px)`,
      }}>
        <h1 style={{
          fontSize: 72,
          fontWeight: 700,
          margin: 0,
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          {title}
        </h1>
        <p style={{
          fontSize: 32,
          marginTop: 16,
          opacity: 0.7,
        }}>
          {subtitle}
        </p>
      </div>

      {/* 曲线图 */}
      {showCurve && (
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        >
          {/* 坐标轴 */}
          <g opacity={interpolate(frame, [30, 60], [0, 0.3], { extrapolateRight: 'clamp' })}>
            {/* Y轴 */}
            <line
              x1={(width - width * 0.6) / 2 - 40}
              y1={height * 0.35}
              x2={(width - width * 0.6) / 2 - 40}
              y2={height * 0.35 + height * 0.28}
              stroke="white"
              strokeWidth="2"
            />
            {/* X轴 */}
            <line
              x1={(width - width * 0.6) / 2 - 40}
              y1={height * 0.35 + height * 0.28}
              x2={(width - width * 0.6) / 2 + width * 0.6 + 40}
              y2={height * 0.35 + height * 0.28}
              stroke="white"
              strokeWidth="2"
            />
            {/* Y轴标签 */}
            <text
              x={(width - width * 0.6) / 2 - 80}
              y={height * 0.35 + height * 0.14}
              fill="white"
              fontSize="24"
              textAnchor="middle"
              transform={`rotate(-90, ${(width - width * 0.6) / 2 - 80}, ${height * 0.35 + height * 0.14})`}
            >
              记忆保持率
            </text>
            {/* X轴标签 */}
            <text
              x={width / 2}
              y={height * 0.35 + height * 0.28 + 40}
              fill="white"
              fontSize="24"
              textAnchor="middle"
            >
              天数
            </text>
          </g>

          {/* 原始遗忘曲线（红色） */}
          <path
            d={drawForgettingCurve()}
            fill="none"
            stroke="#EF4444"
            strokeWidth="4"
            strokeLinecap="round"
            opacity={0.6}
          />

          {/* 优化后的记忆曲线（绿色） */}
          <path
            d={drawOptimizedCurve()}
            fill="none"
            stroke={accentColor}
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={interpolate(frame, [240, 280], [0, 0.9], { extrapolateRight: 'clamp' })}
          />

          {/* 复习时间点 */}
          {reviewPoints.map((point, i) => {
            const pointOpacity = interpolate(
              frame,
              [240 + i * 30, 260 + i * 30],
              [0, 1],
              { extrapolateRight: 'clamp' }
            );

            if (i >= reviewPointProgress) return null;

            const graphWidth = width * 0.6;
            const graphHeight = height * 0.28;
            const graphX = (width - graphWidth) / 2;
            const graphY = height * 0.35;
            const maxDays = 30;

            const x = graphX + (point.day / maxDays) * graphWidth;
            const y = graphY + graphHeight - point.retention * graphHeight;

            return (
              <g key={i} opacity={pointOpacity}>
                <circle
                  cx={x}
                  cy={y}
                  r={12}
                  fill={accentColor}
                  stroke="white"
                  strokeWidth="3"
                />
                <text
                  x={x}
                  y={y - 25}
                  fill="white"
                  fontSize="20"
                  textAnchor="middle"
                  fontWeight="600"
                >
                  {point.label}
                </text>
                <text
                  x={x}
                  y={y + 35}
                  fill={accentColor}
                  fontSize="24"
                  textAnchor="middle"
                  fontWeight="700"
                >
                  {Math.round(point.retention * 100)}%
                </text>
              </g>
            );
          })}
        </svg>
      )}

      {/* 时间线 */}
      <div style={{
        position: 'absolute',
        bottom: '22%',
        left: '50%',
        transform: 'translateX(-50%)',
      }}>
        <div style={{
          display: 'flex',
          gap: 40,
          alignItems: 'flex-start',
        }}>
          {reviewPoints.slice(0, Math.ceil(reviewPointProgress)).map((point, i) => {
            const itemOpacity = interpolate(
              frame,
              [240 + i * 30, 260 + i * 30],
              [0, 1],
              { extrapolateRight: 'clamp' }
            );

            return (
              <div key={i} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: itemOpacity,
              }}>
                <div style={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                  fontWeight: 700,
                  boxShadow: '0 4px 20px rgba(74, 144, 226, 0.4)',
                }}>
                  {i + 1}
                </div>
                <div style={{
                  marginTop: 12,
                  fontSize: 20,
                  fontWeight: 600,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                }}>
                  Day {point.day}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 说明文字 */}
      <div style={{
        position: 'absolute',
        bottom: 40,
        left: 0,
        right: 0,
        textAlign: 'center',
        opacity: interpolate(frame, [420, 460], [0, 1], { extrapolateRight: 'clamp' }),
      }}>
        <div style={{
          display: 'inline-block',
          padding: '24px 40px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 16,
          border: '2px solid rgba(255, 255, 255, 0.2)',
        }}>
          <div style={{
            fontSize: 28,
            fontWeight: 600,
            marginBottom: 8,
          }}>
            💡 关键复习时间点
          </div>
          <div style={{
            fontSize: 22,
            opacity: 0.8,
          }}>
            1天 → 3天 → 7天 → 14天 → 30天
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
